import { Component, ChangeDetectionStrategy, signal, HostListener, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';

@Component({
  selector: 'app-salary-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    MatSidenavModule,
    CdkScrollable,
    BreadcrumbComponent,
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent
  ],
  templateUrl: './salary-layout.component.html',
  styleUrls: ['./salary-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalaryLayoutComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  // True when the currently activated route (or any of its ancestors, e.g.
  // /vouchers/details under the /vouchers parent) opts out of the sidebar
  // via route `data: { hideSidebar: true }` — used for full-screen flows
  // reached from notifications (voucher list/detail, approval screens).
  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  // Define sidebar menu for Salary Module (with role-based permissions resolved dynamically at runtime)
  salaryMenuConfig: SidebarItem[] = [
    { id: 'dash', label: 'salary.sidebar.dash', imgIcon: 'assets/images/dash.png', route: '/salary', exact: true },
    {
      id: 'emp_mgmt',
      label: 'salary.sidebar.emp_mgmt',
      imgIcon: 'assets/images/Employee.png',
      route: '/salary/employees',
      children: [
        { id: 'emp_all', label: 'salary.sidebar.emp_all', route: '/salary/employees' },
        { id: 'emp_acc', label: 'salary.sidebar.emp_acc', route: '/salary/employees/accounts' },
        { id: 'emp_edu', label: 'salary.sidebar.emp_edu', route: '/salary/employees/education' },
        { id: 'emp_doc', label: 'salary.sidebar.emp_doc', route: '/salary/employees/documents' },
        { id: 'emp_nominee', label: 'salary.sidebar.emp_nominee', route: '/salary/employees/nominee' },
        { id: 'app', label: 'salary.sidebar.establishment', route: '/salary/service-book/update/establishment' },
        { id: 'vp', label: 'salary.sidebar.variable_pay', route: '/salary/service-book/update/variable-pay' }
      ]
    },
    {
      id: 'salary',
      label: 'salary.sidebar.salary',
      icon: 'payments',
      route: '/salary/salary/attendance',
      children: [
        { id: 'attendance', label: 'salary.sidebar.attendance', route: '/salary/salary/attendance' },
        { id: 'salary_proc', label: 'salary.sidebar.salary_proc', route: '/salary/salary/salary-processing' }
      ]
    },
    {
      id: 'advance',
      label: 'salary.sidebar.advance',
      icon: 'celebration',
      route: '/salary/festival-advance',
      children: [
        { id: 'festival_advance', label: 'salary.sidebar.festival_advance', route: '/salary/festival-advance' },
        { id: 'festival_advance_vouchers', label: 'salary.sidebar.festival_advance_vouchers', route: '/salary/festival-advance-vouchers' }
      ]
    },
    {
      id: 'arrear', label: 'salary.sidebar.arrear', imgIcon: 'assets/images/book.png', route: '/salary/service-book/update/arrear/da',
      children: [
        { id: 'arrear_da', label: 'salary.sidebar.arrear_da', route: '/salary/service-book/update/arrear/da' },
        { id: 'arrear_acp', label: 'salary.sidebar.arrear_acp', route: '/salary/service-book/update/arrear/acp' },
        { id: 'arrear_salary', label: 'salary.sidebar.arrear_salary', route: '/salary/service-book/update/arrear/salary' }
      ]
    },
    {
      id: 'service_book',
      label: 'salary.sidebar.service_book',
      imgIcon: 'assets/images/book.png',
      route: '/salary/service-book/update',
      children: [
        { id: 'update_service_book', label: 'salary.sidebar.update_service_book', route: '/salary/service-book/update' },
        { id: 'view_service_book', label: 'salary.sidebar.view_service_book', route: '/salary/service-book/view' },
        { id: 'gratuity', label: 'salary.sidebar.gratuity', route: '/salary/account-clerk/gratuity', permission: 'salary:special-cases:read' },
        { id: 'special_cases', label: 'salary.sidebar.leave_encashment', route: '/salary/salary/special-cases' },
        { id: 'travel_allowance', label: 'salary.sidebar.travel_allowance', route: '/salary/travel-allowance', permission: 'salary:special-cases:read' },
        { id: 'medical_bill', label: 'salary.sidebar.medical_bill', route: '/salary/medical-bill', permission: 'salary:special-cases:read' },
        { id: 'annual_report', label: 'salary.sidebar.annual_report', route: '/salary/service-book/annual-report' }
      ]
    },
    {
      id: 'life_insurance',
      label: 'salary.sidebar.life_insurance',
      icon: 'umbrella',
      route: '/salary/life-insurance/lic-contribution',
      children: [
        { id: 'lic_contribution', label: 'salary.sidebar.lic_contribution', route: '/salary/life-insurance/lic-contribution' },
        { id: 'lic_claim', label: 'salary.sidebar.lic_claim', route: '/salary/life-insurance/lic-claim' },
        { id: 'lic_remit', label: 'salary.sidebar.lic_remit', route: '/salary/life-insurance/remit-to-hq', permission: 'salary:special-cases:read' }
      ]
    },
    {
      id: 'ewf',
      label: 'salary.sidebar.ewf',
      icon: 'volunteer_activism',
      route: '/salary/ewf',
      children: [
        { id: 'ewf_fund', label: 'salary.sidebar.ewf_overview', route: '/salary/ewf' },
        { id: 'ewf_settlement', label: 'salary.sidebar.ewf_settlement', route: '/salary/ewf/settlement' },
        { id: 'ewf_remit', label: 'salary.sidebar.ewf_remit', route: '/salary/ewf/remit-to-hq', permission: 'salary:special-cases:read' }
      ]
    },
    {
      id: 'income_tax',
      label: 'salary.sidebar.income_tax',
      icon: 'request_quote',
      route: '/salary/income-tax',
      children: [
        { id: 'income_tax_contribution', label: 'salary.sidebar.income_tax_contribution', route: '/salary/income-tax' },
        { id: 'income_tax_remit', label: 'salary.sidebar.income_tax_remit', route: '/salary/income-tax/remit-to-hq', permission: 'salary:special-cases:read' }
      ]
    },
    { id: 'electricity_bill', label: 'salary.sidebar.electricity_bill', icon: 'bolt', route: '/salary/electricity-bill' },
    { id: 'hr_colony', label: 'salary.sidebar.hr_colony', icon: 'apartment', route: '/salary/hr-colony' },
    { id: 'misc_deduction', label: 'salary.sidebar.misc_deduction', icon: 'receipt', route: '/salary/misc-deduction' },
    { id: 'salary_vouchers', label: 'salary.sidebar.vouchers', icon: 'receipt_long', route: '/salary/vouchers' },
    { id: 'vendor_mgmt', label: 'salary.sidebar.vendor_mgmt', imgIcon: 'assets/images/Employee.png', route: '/salary/vendor-management' },
    {
      id: 'master_setup', label: 'salary.sidebar.master_setup', imgIcon: 'assets/images/setup.png', route: '/salary/master-setup', children: [
        { id: 'sal_rule', label: 'salary.sidebar.sal_rule', route: '/salary/master-setup/salary-rule' },
        { id: 'pay_rule', label: 'salary.sidebar.pay_rule', route: '/salary/master-setup/pay-rule' },
        { id: 'pay_matrix', label: 'salary.sidebar.pay_matrix', route: '/salary/master-setup/pay-matrix' },
        { id: 'cca_matrix', label: 'salary.sidebar.cca_matrix', route: '/salary/master-setup/cca-matrix' },
        { id: 'hra_matrix', label: 'salary.sidebar.hra_matrix', route: '/salary/master-setup/hra-matrix' },
        { id: 'city_class', label: 'salary.sidebar.city_class', route: '/salary/master-setup/city-class' },
        { id: 'budget_hier', label: 'salary.sidebar.budget_hier', route: '/salary/master-setup/budget-hierarchy' },
        { id: 'prod_map', label: 'salary.sidebar.prod_map', route: '/salary/master-setup/product-mapping' },
        { id: 'act_map', label: 'salary.sidebar.act_map', route: '/salary/master-setup/activity-mapping' },
        { id: 'it_slab', label: 'salary.sidebar.it_slab', route: '/salary/master-setup/it-slab' }]
    }
  ];

  ngOnInit() {
    this.resolvePermissionsFromRouter();
  }

  private resolvePermissionsFromRouter(): void {
    const salaryRoute = this.router.config.find(r => r.path === 'salary');
    this.salaryMenuConfig = resolveMenuPermissionsFromRoute(this.salaryMenuConfig, salaryRoute, 'salary');
  }

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }
}
